import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { createRef, PureComponent } from 'react';
import { connect } from 'react-redux';

import { ACCOUNT_CUSTOMER_SERVICE_URL } from 'Route/MyAccount/MyAccount.config';
import IssueDispatcher from 'Store/Issue/Issue.dispatcher';
import OrderDispatcher from 'Store/Order/Order.dispatcher';
import { hideActivePopup } from 'Store/Overlay/Overlay.action';
import { OrdersListType, OrderType } from 'Type/Order.type';
import transformToNameValuePair from 'Util/Form/Transform';
import history from 'Util/History';
import { appendWithStoreCode } from 'Util/Url';

import NewIssueForm from './NewIssueForm.component';

/** @namespace PeggysagePwa/Component/NewIssueForm/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    orderList: state.OrderReducer.orderList
});

/** @namespace PeggysagePwa/Component/NewIssueForm/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    requestOrders: (filter) => OrderDispatcher.requestOrders(dispatch, 1, false, filter),
    getTickets: (options) => IssueDispatcher.getTickets(dispatch, options),
    createTicket: (ticket) => IssueDispatcher.createTicket(dispatch, ticket),
    hideActivePopup: () => dispatch(hideActivePopup())
});

/** @namespace PeggysagePwa/Component/NewIssueForm/Container */
export class NewIssueFormContainer extends PureComponent {
    static propTypes = {
        orderList: OrdersListType,
        requestOrders: PropTypes.func.isRequired,
        getTickets: PropTypes.func.isRequired,
        createTicket: PropTypes.func.isRequired,
        hideActivePopup: PropTypes.func.isRequired,
        order: OrderType
    };

    static defaultProps = {
        orderList: [],
        order: {}
    };

    state = {
        isLoading: false,
        isLoadingOrders: false,
        isOrderFieldVisible: false,
        isOrderSearchVisible: false,
        selectedOrder: {},
        numberInputFile: 0
    };

    containerFunctions = {
        onSubjectFieldChange: this.onSubjectFieldChange.bind(this),
        onOrderFieldFocus: this.onOrderFieldFocus.bind(this),
        onOrderIsSelected: this.onOrderIsSelected.bind(this),
        onOrderSearchInputChange: this.onOrderSearchInputChange.bind(this),
        onFileButtonClick: this.onFileButtonClick.bind(this),
        onCreateSubmitSuccess: this.onCreateSubmitSuccess.bind(this),
        closeOrderSearch: this.closeOrderSearch.bind(this)
    };

    inputAutocompleteRef = createRef();

    // eslint-disable-next-line
    debouncedLoadOrders = debounce(this.loadOrders, 500);

    componentDidMount() {
        const { order } = this.props;

        if (order) {
            this.setState({ selectedOrder: order });
        }

        this.initFields();
    }

    getSubjects() {
        return [
            {
                id: 'order',
                value: __('Ma commande').toString(),
                label: __('Ma commande'),
                isOrderIdRequired: true
            },
            {
                id: 'product',
                value: __('Demande produit').toString(),
                label: __('Demande produit'),
                isOrderIdRequired: false
            },
            {
                id: 'account',
                value: __('Mon compte client').toString(),
                label: __('Mon compte client'),
                isOrderIdRequired: false
            },
            {
                id: 'shipping',
                value: __('Suivi de livraison').toString(),
                label: __('Suivi de livraison'),
                isOrderIdRequired: true
            },
            {
                id: 'other',
                value: __('Autres').toString(),
                label: __('Autres'),
                isOrderIdRequired: false
            }
        ];
    }

    getSubjectOptionsList() {
        const subjects = this.getSubjects();

        // Return array of OptionType
        return subjects.map((subject) => {
            const { id, value, label } = subject;
            return { id, value, label };
        });
    }

    initFields() {
        const initialSubjectValue = this.getSubjectOptionsList()[0].value;

        if (initialSubjectValue) {
            this.onSubjectFieldChange(initialSubjectValue);
        }
    }

    onSubjectFieldChange(value) {
        const { orderList } = this.props;

        const subjects = this.getSubjects();

        const isOrderIdRequired = subjects.find((subject) => subject.value === value)?.isOrderIdRequired || false;

        this.setState({ isOrderFieldVisible: isOrderIdRequired });

        // If order field is visible, load last orders on first time
        if (isOrderIdRequired && (!orderList || !orderList.length)) {
            this.loadOrders();
        }
    }

    onOrderFieldFocus(event) {
        const { isOrderSearchVisible } = this.state;

        if (event.type === 'focus') {
            event.preventDefault();
            event.stopPropagation();
        } else {
            this.setState({ isOrderSearchVisible: !isOrderSearchVisible }, () => {
                // Put focus on autocomplete field when order search is opened
                const elem = this.inputAutocompleteRef && this.inputAutocompleteRef.current;
                if (!isOrderSearchVisible && elem) {
                    elem.focus();
                }
            });
        }
    }

    closeOrderSearch() {
        this.setState({ isOrderSearchVisible: false });
    }

    onOrderIsSelected(order) {
        this.setState({
            selectedOrder: order,
            isOrderSearchVisible: false
        });
    }

    onOrderSearchInputChange(e) {
        const { target: { value } } = e;

        this.debouncedLoadOrders(value);
    }

    loadOrders(value) {
        const { requestOrders } = this.props;

        const filter = value ? {
            number: {
                match: value
            },
            pageSize: 10
        } : {};

        this.setState({ isLoadingOrders: true });

        requestOrders(filter).then(
            /** @namespace PeggysagePwa/Component/NewIssueForm/Container/NewIssueFormContainer/loadOrders/requestOrders/then */
            () => {
                this.setState({ isLoadingOrders: false });
            }
        );
    }

    onFileButtonClick() {
        const { numberInputFile } = this.state;

        this.setState({ numberInputFile: numberInputFile + 1 });
    }

    onCreateSubmitSuccess(form, fields) {
        const { createTicket, getTickets, hideActivePopup } = this.props;
        const { numberInputFile } = this.state;
        const { location: { pathname } } = history;

        this.setState({ isLoading: true });

        const nameValuePairs = transformToNameValuePair(fields);

        const {
            subject,
            // order_increment_id,
            order_id,
            message
        } = nameValuePairs;

        const files = [];

        // eslint-disable-next-line fp/no-loops,fp/no-let
        for (let i = 0; i < numberInputFile; i++) {
            if (nameValuePairs[`file_${ i }`]) {
                const fileDataObj = JSON.parse(nameValuePairs[`file_${ i }`]);
                const fileObj = fileDataObj ? {
                    filename: fileDataObj.file_name,
                    content: fileDataObj.file_data
                } : null;

                files.push(fileObj);
            }
        }

        // this.forceUpdate();

        const options = {
            subject,
            order_id: +order_id,
            message,
            files
        };

        createTicket(options).then(
            /** @namespace PeggysagePwa/Component/NewIssueForm/Container/NewIssueFormContainer/onCreateSubmitSuccess/createTicket/then */
            () => {
                this.setState({ isLoading: false });

                hideActivePopup();

                if (pathname.includes(ACCOUNT_CUSTOMER_SERVICE_URL)) {
                    // Tickets page => Reload ticket list
                    getTickets();
                } else {
                    // Other page => Go to ticket list page
                    history.push(appendWithStoreCode(ACCOUNT_CUSTOMER_SERVICE_URL));
                }
            }
        );
    }

    containerProps() {
        const {
            orderList: { items } = {}
        } = this.props;
        const {
            isLoading,
            isLoadingOrders,
            isOrderFieldVisible,
            isOrderSearchVisible,
            selectedOrder,
            numberInputFile
        } = this.state;

        return {
            orders: items || [],
            subjectOptionsList: this.getSubjectOptionsList(),
            isLoading,
            isLoadingOrders,
            isOrderFieldVisible,
            isOrderSearchVisible,
            selectedOrder,
            numberInputFile,
            inputAutocompleteRef: this.inputAutocompleteRef
        };
    }

    render() {
        return (
            <NewIssueForm
              { ...this.containerFunctions }
              { ...this.containerProps() }
            />
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(NewIssueFormContainer);
